import _ from 'lodash'
import { localAxios } from './global'

/**
 * Базовый класс для взаимодействия с CrudApiTrait
 */
export default class CrudApi {
  constructor (entry, axios = localAxios) {
    this.entry = entry
    this.axios = axios
  }

  /**
   * Загружает список записей для страницы пагинации.
   *
   * @param {number} page номер страницы
   * @param {number} size количество записей на одной страницу
   * @param {*} sorting параметры сортировки
   * @param {*} filter фильтр
   * @param {String[]} include запрашиваемые блоки
   * @returns {{count: *, data: *}}
   * @see https://github.com/andersao/l5-repository
   */
  async loadPage (page, size, sorting = null, filter = null, include = []) {
    let {data} = await this.load({page, size}, sorting, filter, include)

    return {
      count: data.meta.pagination.total,
      data: data.data
    }
  }

  async load (params, sorting = null, filter = null, include = []) {
    let _filter = this._prepareFilter(filter)
    let _sorting = this._prepareSorting(sorting)

    let _params = {...params, ..._filter, ..._sorting}

    if (include) {
      _params['include'] = include
    }

    return this.axios.get(this.entry, {params: _params})
  }

  /**
   * Загружает список записей для autocomplete-поиска.
   *
   * @param {string} query строка поиска
   * @param {string|[string]} searchFields список полей по которым ведется поиск
   * @param {*} sorting параметры сортировки
   * @param {*} filter дополнительный фильтр   *
   * @param {number} max максимальное количество записей
   * @param {boolean|string|[string]} pick определяет какие поля (кроме id) возвращать в ответе.
   * Можно передать название одного поля, список полей в массиве, или true тогда в ответе будут
   * только поля указанные в fields.
   * @returns {{data: *}}
   */
  async autocomplete (query, searchFields, sorting = null, filter = null, max = 100, pick = true) {
    let _filter = this._prepareFilter(filter)
    let _sorting = this._prepareSorting(sorting)

    let params = {query, searchFields, max, ..._filter, ..._sorting}

    let {data} = await this.axios.get(this.entry + '/autocomplete', {params})

    // не стал делать это на серверной стороне, потому что придется кастомизировать трансформер и хочется оставить
    // ответы сервера всегда одинаковыми.
    let pickFields = []

    if (pick === true) {
      // оставляем только поля которые есть в searchFields
      pickFields = [...searchFields]
    } else if (_.isString(pick)) {
      pickFields = [pick]
    }

    if (pickFields.indexOf('id') === -1) {
      pickFields.push('id')
    }

    return {
      data: _.map(data.data, (item) => _.pick(item, pickFields))
    }
  }

  /**
   * Загружает одну запись.
   *
   * @param {number} id ID записи
   * @param {String[]} include запрашиваемые блоки
   * @param {*} filter дополнительный фильтр
   * @returns {{data: *}}
   */
  async getItem (id, include = [], filter = null) {
    let _filter = this._prepareFilter(filter)
    let params = {..._filter}

    if (include) {
      params['include'] = include
    }

    let {data} = await this.axios.get(this.entry + '/' + id, {params})

    return {
      data: data.data
    }
  }

  /**
   * Добавляет или обновляет запись.
   *
   * @param {*} item данные
   * @returns {{data: *}} ответ сервера (data - не обязательнол содержит весь item)
   */
  async insertItem (item) {
    let {data} = await this.axios.post(this.entry, {item})

    return {
      data: data.data
    }
  }

  /**
   * Обновляет запись.
   *
   * @param {number} id ID записи
   * @param {*} item данные
   * @returns {{data: *}} ответ сервера (data - не обязательно содержит весь item)
   */
  async updateItem (id, item) {
    let {data} = await this.axios.put(this.entry + '/' + id, {item})

    return {
      data: data.data
    }
  }

  /**
   * Удаляет запись.
   *
   * @param {number} id ID записи
   * @returns {{count: number}} где count количество удаленных записей
   */
  async deleteItem (id) {
    let {data} = await this.axios.delete(this.entry + '/' + id)

    return {
      count: data.data
    }
  }

  /**
   * Удаляет записи.
   *
   * @param {number[]} ids массив содержащий ID записей
   * @returns {{count: number}} количество удаленных записей
   */
  async deleteItems (ids) {
    let {data} = await this.axios.post(this.entry + '/mass/delete', {ids})

    return {
      count: data.data
    }
  }

  /**
   * Устанавливает видимость.
   *
   * @param {boolean} visibility видимость
   * @param {number[]} ids массив содержащий ID записей
   * @returns {{count: number}} количество удаленных записей
   */
  async setItemsVisibility (visibility, ids) {
    let {data} = await this.axios.post(this.entry + '/mass/visibility', {visibility, ids})

    return {
      count: data.data
    }
  }

  /**
   * Убирает из фильтра незаполненные поля и преобразует его к виду пригодному для использования в RequestCriteria
   * https://github.com/andersao/l5-repository#example-the-criteria
   *
   * @param {*} filter фильтр
   */
  _prepareFilter (filter) {
    if (filter === null) {
      return {}
    }

    // убираем null значения (false, 0)
    let _filter = _.pickBy(filter, (value) => {
      return value !== null && value !== ''
    })

    let search = []

    _.forEach(_filter, (val, key) => {
      search.push(key + ':' + val)
    })

    if (search.length === 0) {
      return {}
    }

    return {search: search.join(';')}
  }

  /**
   * Преобразует данные переданные для сортировки в вид пригодный для использования в RequestCriteria
   * @param sorting
   * @returns {{}}
   * @private
   */
  _prepareSorting (sorting) {
    if (sorting === null) {
      return {}
    }

    return {orderBy: sorting.field, sortedBy: sorting.order}
  }
}



// WEBPACK FOOTER //
// ./dev-packages/Core/assets/CrudApi.js